/**
 * etemadonline — article ("news") page script.
 *
 * Vanilla JS: no jQuery, no AngularJS, no video.js. The only third-party globals
 * are Splide (sliders) and PollForm (XCMS common-js), both guarded before use.
 *
 * FAILURE ISOLATION: every module runs inside safe(), so a throw in one feature can
 * never stop the rest of the page's JS from initialising. Add new behaviour as a
 * MODULES entry, never as loose top-level code.
 *
 * Shares its structure — and the SHARED PRELUDE below verbatim — with main.js.
 * Modules that exist in both files (emergencyClose, shortLinkCopy, shareToggle,
 * responsiveTables, mobileNav, linkHelper, polls) are written identically so that
 * diffing the two files shows only genuine differences.
 */
(function () {
  "use strict";

  // ===== SHARED PRELUDE — byte-identical in main.js and main-news.js. =========
  // build/js.js fails the build if these two blocks ever drift apart.

  /** Run one module; log and swallow anything it throws. */
  function safe(name, fn) {
    try {
      fn();
    } catch (err) {
      if (window.console && console.error) {
        console.error(
          "[" + SCRIPT_NAME + '] module "' + name + '" failed:',
          err,
        );
      }
    }
  }

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  // Deliberately NOT named `$`/`$$`, so that grepping for a dollar-paren stays a
  // reliable way to prove this tree is jQuery-free.
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /** Toggle an element between `display:<shown>` and `display:none`. */
  function toggleDisplay(el, shownValue) {
    if (!el) return;
    var shown = shownValue || "block";
    var current = window.getComputedStyle(el).display;
    el.style.display = current === "none" ? shown : "none";
  }

  var SLIDE_SPEEDS = { slow: 600, fast: 200, _default: 400 };

  function slideDuration(speed) {
    if (typeof speed === "number") return speed;
    return SLIDE_SPEEDS[speed] || SLIDE_SPEEDS._default;
  }

  var SLIDE_BOX = [
    "height",
    "paddingTop",
    "paddingBottom",
    "marginTop",
    "marginBottom",
  ];

  function isHidden(el) {
    return window.getComputedStyle(el).display === "none";
  }

  function animateSlide(el, opening, duration) {
    // Cancel a half-finished slide so rapid clicks don't stack up.
    if (el._slideAnim) el._slideAnim.cancel();

    var previousDisplay = el.style.display;
    if (opening) el.style.display = "";
    if (opening && isHidden(el)) el.style.display = "block";

    var cs = window.getComputedStyle(el);
    var open = {
      height: el.offsetHeight + "px",
      paddingTop: cs.paddingTop,
      paddingBottom: cs.paddingBottom,
      marginTop: cs.marginTop,
      marginBottom: cs.marginBottom,
    };
    var closed = {};
    SLIDE_BOX.forEach(function (p) {
      closed[p] = "0px";
    });

    var from = opening ? closed : open;
    var to = opening ? open : closed;
    from.overflow = to.overflow = "hidden";

    // No Web Animations support (or reduced motion): jump straight to the end state.
    if (!el.animate) {
      el.style.display = opening ? el.style.display : "none";
      if (!opening) el.style.display = "none";
      return;
    }

    var anim = el.animate([from, to], {
      duration: duration,
      easing: "ease-in-out",
    });
    el._slideAnim = anim;
    anim.onfinish = function () {
      el._slideAnim = null;
      if (opening) {
        el.style.display = previousDisplay === "none" ? "" : previousDisplay;
        if (isHidden(el)) el.style.display = "block";
      } else {
        el.style.display = "none";
      }
    };
  }

  function slideToggle(el, speed) {
    if (!el) return;
    animateSlide(el, isHidden(el), slideDuration(speed));
  }

  var MOBILE_NAV_QUERY = "(max-width: 799px)";

  function isMobileNav() {
    return !window.matchMedia || window.matchMedia(MOBILE_NAV_QUERY).matches;
  }

  function initMobileNav() {
    var nav = qs("#nav-toggle");
    var menuButton = qs(".show-menu");

    if (nav && menuButton) {
      menuButton.addEventListener("click", function () {
        toggleDisplay(nav);
      });

      if (window.matchMedia) {
        var query = window.matchMedia(MOBILE_NAV_QUERY);
        var reset = function () {
          if (!query.matches) nav.style.display = "";
        };
        if (query.addEventListener) query.addEventListener("change", reset);
        else if (query.addListener) query.addListener(reset);
      }
    }

    qsa(".submenu-mobile-icn").forEach(function (icon) {
      icon.addEventListener("click", function (e) {
        if (!isMobileNav()) return;
        e.stopPropagation();
        e.preventDefault();
        var submenu = icon.parentNode && icon.parentNode.children[2];
        toggleDisplay(submenu);
      });
    });
  }

  /** The full-screen search overlay behind the header magnifier. */
  function initSearchModal() {
    var openButton = document.getElementById("btn-search");
    var closeButton = document.getElementById("close-search");
    var block = document.getElementById("topsearch-block");
    var modal = qs(".search-modal");
    var input = document.getElementById("input-search");

    if (!block || !modal) return;

    if (openButton) {
      openButton.addEventListener("click", function () {
        block.classList.remove("hidesearch");
        slideToggle(modal);
        if (input) input.focus();
      });
    }
    if (closeButton) {
      closeButton.addEventListener("click", function () {
        block.classList.add("hidesearch");
        // Let the ✕ rotate back into a + before the overlay slides away.
        setTimeout(function () {
          slideToggle(modal);
        }, 500);
      });
    }
  }

  /** Fisher-Yates, in place. */
  function shuffle(array) {
    for (var i = array.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = array[i];
      array[i] = array[j];
      array[j] = tmp;
    }
    return array;
  }

  function renderLinkHelper(options) {
    var container = document.getElementById(options.containerId);
    if (!container) return;

    fetch("/linkhelpers", { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("linkhelpers HTTP " + res.status);
        return res.json();
      })
      .then(function (data) {
        var limit = typeof NewsListLimit === "number" ? NewsListLimit : 0;
        var special = data.special.slice(
          0,
          Math.min(data.special.length, limit),
        );
        var others = shuffle(data.others).slice(0, limit - special.length);

        container.innerHTML = special
          .map(function (item) {
            return options.renderItem(item, true);
          })
          .concat(
            others.map(function (item) {
              return options.renderItem(item, false);
            }),
          )
          .join("");

        if (!special.length && !others.length) {
          var block = qs(options.emptySelector);
          if (block) block.style.display = "none";
        }
      })
      .catch(function (err) {
        if (window.console && console.error) {
          console.error("[" + SCRIPT_NAME + "] linkHelper:", err);
        }
      });
  }

  /** Mount Splide carousels from a [selector, options] table. */
  function mountSliders(sliders) {
    if (typeof Splide === "undefined") return;
    sliders.forEach(function (entry) {
      if (!qs(entry[0])) return;
      // One bad slider must not stop the others from mounting.
      safe("slider " + entry[0], function () {
        new Splide(entry[0], entry[1]).mount();
      });
    });
  }

  /** Wire the XCMS PollForm for a {wrapper, form, status} triple. */
  function initPoll(poll) {
    if (typeof PollForm === "undefined") return;
    if (!document.getElementsByClassName(poll.wrapper).length) return;
    var form = document.getElementById(poll.form);
    if (!form) return;

    function setStatus(color, text, show) {
      var el = document.getElementById(poll.status);
      if (!el) return;
      if (show) el.style.display = "block";
      el.style.color = color;
      el.innerHTML = text;
    }

    new PollForm(form, {
      onSubmit: function () {
        setStatus("#181b22", "در حال ارسال نظر...", false);
      },
      onError: function () {
        setStatus(
          "#eb0017",
          "شما پیش از این یکبار در نظرسنجی شرکت کردید!",
          true,
        );
      },
      onSuccess: function () {
        setStatus("#1BCF01", "رای با موفقیت ثبت شد.", true);
      },
    });
  }
  // ===== END SHARED PRELUDE ===================================================

  var SCRIPT_NAME = "main-news.js";

  /** The banner options shared by every carousel on this page. */
  function banner(interval) {
    return {
      autoplay: true,
      interval: interval,
      type: "loop",
      direction: "rtl",
      adaptiveHeight: false,
    };
  }

  // ---------------------------------------------------------------------------
  // modules — each one is independent and individually fault-tolerant
  // ---------------------------------------------------------------------------

  var MODULES = {
    /** Embedded Pelikan iframes self-report their height via postMessage. */
    pelikanIframes: function () {
      var iframes = qsa(".pelikan_iframe");
      if (!iframes.length) return;
      window.addEventListener("message", function (event) {
        if (!event.data || !event.data.height || !event.data.src) return;
        iframes.forEach(function (iframe) {
          if (iframe.src === event.data.src) {
            iframe.style.height = event.data.height + "px";
          }
        });
      });
    },

    /** Header menu + sub-menu toggles. Shared with main.js. */
    mobileNav: initMobileNav,

    /** Header search overlay open/close. Shared with main.js. */
    searchModal: initSearchModal,

    /** Dismiss the breaking-news banner. */
    emergencyClose: function () {
      var button = document.getElementById("emergency-close");
      var box = document.getElementById("emergency-box");
      if (!button || !box) return;
      button.addEventListener("click", function () {
        box.style.opacity = "0";
        box.style.visibility = "hidden";
      });
    },

    /** Copy the article short link, with a transient tooltip. */
    shortLinkCopy: function () {
      var button = qs(".short_link_form .copy_btn");
      if (!button) return;
      button.addEventListener("click", function (event) {
        event.preventDefault();
        var input = document.getElementById("short_link");
        var tooltip = qs(".tooltiptext");
        if (!input || !tooltip) return;
        input.select();
        document.execCommand("copy");
        tooltip.style.display = "block";
        setTimeout(function () {
          tooltip.style.display = "none";
        }, 2500);
      });
    },

    /** Article share fly-out. */
    shareToggle: function () {
      var button = qs(".show-share");
      if (!button) return;
      button.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleDisplay(qs(".article-share"));
      });
    },

    /** Wrap article tables so they can scroll on narrow screens. */
    responsiveTables: function () {
      if (!qs(".echo-detail")) return;
      qsa(".echo-detail table").forEach(function (table) {
        var wrapper = document.createElement("div");
        wrapper.classList.add("table_res");
        table.parentNode.replaceChild(wrapper, table);
        wrapper.appendChild(table);
      });
    },

    /** Article-page carousels. */
    sliders: function () {
      mountSliders([
        [".suggest-video", banner(4000)], // FRONT_B
        ["#gallery_twig", banner(4000)],
        ["#gallery_attach", banner(4000)],
      ]);
    },

    inlineBoxes: function () {
      var paragraphs = qsa(".echo-detail p:not(table p, blockquote p)");

      // [element id, paragraph index, placement relative to that paragraph]
      var SLOTS = [
        ["inline-first-adv", 1, "after"],
        ["inline-second-adv", 4, "after"],
        ["inline-third-adv", 7, "after"],
        ["inline-fourth-adv", 10, "after"],
        ["inline-fifth-adv", 13, "after"],
        ["inline-sixth-adv", 16, "after"],
        ["inline-seventh-adv", 19, "after"],
        ["inline-eighth-adv", 22, "after"],
        ["inline-ninth-adv", 25, "after"],
        ["inline-tenth-adv", 28, "after"],
        ["inline-mobile-adv", 1, "before"],
        ["inline-related", 2, "before"],
        ["inline-read-more", 4, "before"],
      ];

      SLOTS.forEach(function (slot) {
        var el = document.getElementById(slot[0]);
        if (!el) return;

        var index = slot[1];
        if (paragraphs.length <= index) {
          el.style.display = "none";
          return;
        }

        var anchor = paragraphs[index];
        // `insertBefore(el, null)` appends, which is the original behaviour when
        // the anchor paragraph is the last child.
        var reference = slot[2] === "after" ? anchor.nextSibling : anchor;
        anchor.parentNode.insertBefore(el, reference);
      });
    },

    /** "Related links" block. */
    linkHelper: function () {
      if (!document.getElementsByClassName("link_helper_parent").length) return;

      var ARROW_ICON =
        '<div class="type-icon">' +
        '<svg class="arrow" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="0.38em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 192 512"><path d="M192 127.338v257.324c0 17.818-21.543 26.741-34.142 14.142L29.196 270.142c-7.81-7.81-7.81-20.474 0-28.284l128.662-128.662c12.599-12.6 34.142-3.676 34.142 14.142z" fill="#393939"></path></svg>' +
        "</div>";

      renderLinkHelper({
        containerId: "link_helper",
        emptySelector: ".link_helper_parent",
        renderItem: function (item, isSpecial) {
          return (
            "<li>" +
            ARROW_ICON +
            '<a rel="nofollow" target="_blank"' +
            (isSpecial ? ' class="helper_special"' : "") +
            ' href="' +
            item.url +
            '"' +
            'title="' +
            item.title +
            '">' +
            item.title +
            "</a></li>"
          );
        },
      });
    },

    polls: function () {
      initPoll({
        wrapper: "desktop_poll",
        form: "pollForm",
        status: "pollStatus",
      });
    },
  };

  // ---------------------------------------------------------------------------
  // boot
  // ---------------------------------------------------------------------------

  onReady(function () {
    Object.keys(MODULES).forEach(function (name) {
      safe(name, MODULES[name]);
    });
  });
})();
